import React, { useState } from "react";
import type { OtcPopupProduct } from "data-types";
import QuantityControl from "../../QuantityControl/QuantityControl";
import Information from "../../../icons/Information.svg";
import InformationGreen from "../../../icons/Information-Green.svg";
import InformationOrange from "../../../icons/Information-Orange.svg";
import { clientConfig } from "../../../config";
import * as translator from "../../../utils/translator";
import { getPrice } from "../../../utils/translator";
import { useCartContext } from "../../../customHooks/useCart";
import { useOtcQuantityControl } from "./useOtcQuantityControl";

type Props = {
  product: OtcPopupProduct;
  language: string;
  isCarePlus: boolean;
  onIncrement: (product: OtcPopupProduct, currentQuantity: number) => void;
  onDecrement: (product: OtcPopupProduct) => void;
  onInfoToggle: (product: OtcPopupProduct, open: boolean) => void;
};

const OTCPopupProductCard: React.FC<Props> = ({ product, language, isCarePlus, onIncrement, onDecrement, onInfoToggle }) => {
  const { cartCurrency } = useCartContext();
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const { quantity, isIncrementDisabled } = useOtcQuantityControl(product);

  return (
    <div className="otc-popup-v3-card">
      <div className="otc-popup-v3-card-main">
        <img className="otc-popup-v3-card-image" src={clientConfig.IMAGES_URL + product.imgSrc} alt="" />
        <div className="otc-popup-v3-card-details">
          <p className="otc-popup-v3-card-title" data-testid="otc-popup-v3-card-title">{product.title}</p>
          {product.custom_quantity_title ? (
            <p className="otc-popup-v3-card-content">{translator.translate(language, "shipping", "OTCPopupV3/content_label")}: {product.custom_quantity_title}</p>
          ) : null}
          <p className="otc-popup-v3-card-price">
            {getPrice(product.price, cartCurrency.locale, cartCurrency.rate)}
            {product.is_discounted && product.percentage_discount !== null ? (
              <span className="otc-popup-v3-card-discount">{product.percentage_discount}% {translator.translate(language, "shipping", "OTCPopupV3/discount_suffix")}</span>
            ) : null}
          </p>
          {product.is_discounted && product.price_before_discount !== null ? (
            <p className="otc-popup-v3-card-price-before" data-testid="otc-popup-v3-price-before">{getPrice(product.price_before_discount, cartCurrency.locale, cartCurrency.rate)}</p>
          ) : null}
        </div>
        <div className="otc-popup-v3-card-controls">
          <button
            type="button"
            data-testid={`otc-popup-v3-card-${product.id}-info`}
            className={`otc-popup-v3-info${isInfoOpen ? " otc-popup-v3-info-open" : ""}`}
            onClick={() => { const next = !isInfoOpen; setIsInfoOpen(next); onInfoToggle(product, next); }}
          >
            <img src={isInfoOpen ? Information : isCarePlus ? InformationOrange : InformationGreen} alt="i" />
          </button>
          <QuantityControl
            idPrefix={`otc-popup-v3-card-${product.id}-quantity-control`}
            quantity={quantity}
            incrementButtonOnClick={() => onIncrement(product, quantity)}
            decrementButtonOnClick={() => onDecrement(product)}
            isIncrementDisabled={isIncrementDisabled}
          />
        </div>
      </div>
      {isInfoOpen ? <p className="otc-popup-v3-card-description">{product.description}</p> : null}
    </div>
  );
};

export default OTCPopupProductCard;
